/**
 * Exchange icons — actual PNG logos from /exchanges/<ex>.png (public folder).
 * Falls back to a coloured letter chip if the image fails to load.
 */
import React, { useState, useEffect } from 'react';
import { isMarginVenue, marginBaseExchange, marginKind } from '../utils/exchanges';
import { dexSwapUrl, isDexVenue } from '../utils/dexTokens';
import i18n from '../i18n';

// Слова в подписях площадок («Binance спот»). Язык меняется с перезагрузкой
// страницы, поэтому перевод на уровне модуля допустим (см. i18n/index.ts).
const L_SPOT = i18n.t('exchangeIcon:spot');
const L_FUT = i18n.t('exchangeIcon:futShort');

const EX_META: Record<string, { color: string; letter: string; label: string }> = {
  binance:     { color: '#F3BA2F', letter: 'B', label: 'Binance' },
  bybit:       { color: '#F7A600', letter: 'Y', label: 'Bybit' },
  okx:         { color: '#FFFFFF', letter: 'X', label: 'OKX' },
  gate:        { color: '#2354E6', letter: 'G', label: 'Gate' },
  mexc:        { color: '#1972E2', letter: 'M', label: 'MEXC' },
  bitget:      { color: '#1DA2B4', letter: 'S', label: 'Bitget' },
  phemex:      { color: '#39C4B6', letter: 'P', label: 'Phemex' },
  aster:       { color: '#7C3AED', letter: 'A', label: 'Aster' },
  bingx:       { color: '#2954FE', letter: 'X', label: 'BingX' },
  hyperliquid: { color: '#97FCE4', letter: 'H', label: 'Hyperliquid' },
  // Builder-дексы Hyperliquid: наружу это ОДНА биржа, декс — только пометка
  // (иконка базовая HL + буква декса в углу, метки совпадают с текстом алертов).
  hl_xyz:      { color: '#97FCE4', letter: 'X', label: 'Hyperliquid · xyz' },
  hl_para:     { color: '#97FCE4', letter: 'P', label: 'Hyperliquid · para' },
  hl_hyna:     { color: '#97FCE4', letter: 'H', label: 'Hyperliquid · hyna' },
  hl_mkts:     { color: '#97FCE4', letter: 'M', label: 'Hyperliquid · mkts' },
  hl_io:       { color: '#97FCE4', letter: 'I', label: 'Hyperliquid · io' },
  kucoin:      { color: '#23AF91', letter: 'K', label: 'KuCoin' },
  kraken:      { color: '#5848D6', letter: 'K', label: 'Kraken' },
  // Coinbase: фьючерсы (`coinbase` — американские перпы CDE, «BNB PERP» в
  // приложении) и спот Advanced Trade (`coinbase_spot`, пары за USD) — только данные.
  coinbase:    { color: '#0052FF', letter: 'C', label: `Coinbase ${L_FUT}` },
  htx:         { color: '#0086F8', letter: 'H', label: 'HTX' },
  bitmart:     { color: '#23A079', letter: 'B', label: 'BitMart' },
  coinex:      { color: '#1FBA67', letter: 'C', label: 'CoinEx' },
  xt:          { color: '#0089FF', letter: 'X', label: 'XT.com' },
  bitunix:     { color: '#FFD300', letter: 'B', label: 'BitUnix' },
  btse:        { color: '#FED83D', letter: 'B', label: 'BTSE' },
  toobit:      { color: '#FFA000', letter: 'T', label: 'Toobit' },
  paradex:     { color: '#52DEAD', letter: 'P', label: 'Paradex' },
  pacifica:    { color: '#7CFFB2', letter: 'P', label: 'Pacifica' },
  lighter:     { color: '#A0E7E5', letter: 'L', label: 'Lighter' },
  // Lighter на Robinhood Chain (расчёт в USDG). Иконка — пока lighter.svg.
  lighterhood: { color: '#CCFF00', letter: 'L', label: 'Lighter RH' },
  apex:        { color: '#06D8B7', letter: 'A', label: 'ApeX' },
  edgex:       { color: '#FF9F1C', letter: 'E', label: 'edgeX' },
  edgex_arc:   { color: '#FF9F1C', letter: 'A', label: 'edgeX Arc' },
  orderly:     { color: '#8B5CF6', letter: 'O', label: 'Orderly' },
  risex:       { color: '#E11D48', letter: 'R', label: 'RISEx' },
  extended:    { color: '#7B8DFF', letter: 'X', label: 'Extended' },
  // Arcus (перп-DEX на Robinhood Chain). Иконка — apple-touch-icon с app.arcus.xyz.
  arcus:       { color: '#C8F24B', letter: 'A', label: 'Arcus' },
  grvt:        { color: '#F25C54', letter: 'G', label: 'GRVT' },
  lbank:       { color: '#2ECC71', letter: 'L', label: 'LBank' },
  gatedex:     { color: '#2354E6', letter: 'G', label: 'Gate DEX' },
  nado:        { color: '#FF6B35', letter: 'N', label: 'Nado' },
  blofin:      { color: '#3B82F6', letter: 'B', label: 'BloFin' },
  poloniex:    { color: '#1ABE7A', letter: 'P', label: 'Poloniex' },
  coinw:       { color: '#F59E0B', letter: 'C', label: 'CoinW' },
  ascendex:    { color: '#06B6D4', letter: 'A', label: 'AscendEX' },
  bitstamp:    { color: '#5DA130', letter: 'B', label: 'Bitstamp' },
  pionex:      { color: '#5E73FF', letter: 'P', label: 'Pionex' },
  bluefin:     { color: '#41D6FF', letter: 'B', label: 'Bluefin' },
  nonkyc:      { color: '#FFD700', letter: 'N', label: 'NonKYC' },
  darkex:      { color: '#FF5A1F', letter: 'D', label: 'Darkex' },
  whitebit:    { color: '#1F75BC', letter: 'W', label: 'WhiteBIT' },
  bitmex:      { color: '#D32027', letter: 'B', label: 'BitMEX' },
  cryptocom:   { color: '#063EAA', letter: 'C', label: 'Crypto.com' },
  backpack:    { color: '#D34B47', letter: 'B', label: 'Backpack' },
  dydx:        { color: '#6966FF', letter: 'D', label: 'dYdX v4' },
  aevo:        { color: '#FFFFFF', letter: 'Æ', label: 'Aevo' },
  sunperp:     { color: '#F5A623', letter: 'S', label: 'SunPerp' },

  // СПОТ-площадки. Отдельные «биржи» в матрице, поэтому и в UI отдельные записи:
  // в связке «binance_spot → binance» иначе не понять, где спот, а где перп.
  binance_spot: { color: '#F3BA2F', letter: 'B', label: `Binance ${L_SPOT}` },
  bybit_spot:   { color: '#F7A600', letter: 'Y', label: `Bybit ${L_SPOT}` },
  okx_spot:     { color: '#FFFFFF', letter: 'X', label: `OKX ${L_SPOT}` },
  gate_spot:    { color: '#2354E6', letter: 'G', label: `Gate ${L_SPOT}` },
  mexc_spot:    { color: '#1972E2', letter: 'M', label: `MEXC ${L_SPOT}` },
  bitget_spot:  { color: '#1DA2B4', letter: 'S', label: `Bitget ${L_SPOT}` },
  kucoin_spot:  { color: '#23AF91', letter: 'K', label: `KuCoin ${L_SPOT}` },
  lbank_spot:   { color: '#2ECC71', letter: 'L', label: `LBank ${L_SPOT}` },
  hyperliquid_spot: { color: '#97FCE4', letter: 'H', label: `Hyperliquid ${L_SPOT}` },
  // Иконка — kraken.svg через spotBaseExchange(); ключ у спота СВОЙ (kraken.com).
  kraken_spot:  { color: '#7132F5', letter: 'K', label: `Kraken ${L_SPOT}` },
  // NonKYC (nonkyc.io, 30.09) — только котировки/стаканы; иконка nonkyc.webp
  // через spotBaseExchange().
  nonkyc_spot:  { color: '#FFD700', letter: 'N', label: `NonKYC ${L_SPOT}` },
  // USDC-рынки NonKYC (16.10) — отдельная площадка, только сигналы.
  nonkycusdc_spot: { color: '#FFD700', letter: 'N', label: `NonKYC USDC ${L_SPOT}` },
  // Darkex (darkex.com, 03.10) — только спот, котировки/стаканы; иконка
  // darkex.png через spotBaseExchange().
  darkex_spot:  { color: '#FF5A1F', letter: 'D', label: `Darkex ${L_SPOT}` },
  // Backpack (20.10) — только токенизированные акции (MU.US_USDC), котировки/
  // стаканы; иконка backpack.png через spotBaseExchange().
  backpack_spot: { color: '#D34B47', letter: 'B', label: `Backpack ${L_SPOT}` },
  // Lighter (22.10) — спот за USDC (ETH, LIT, XAUT…), котировки/стаканы;
  // иконка lighter.svg через spotBaseExchange().
  lighter_spot: { color: '#A0E7E5', letter: 'L', label: `Lighter ${L_SPOT}` },
  // Ончейн-спот (04.10): котировки агрегаторов Jupiter / KyberSwap, только сигналы.
  dexsol_spot:  { color: '#9945FF', letter: 'S', label: 'DEX Solana' },
  dexbsc_spot:  { color: '#F0B90B', letter: 'B', label: 'DEX BSC' },
  dexbase_spot: { color: '#0052FF', letter: 'B', label: 'DEX Base' },
  dexegld_spot: { color: '#23F7DD', letter: 'X', label: 'DEX MultiversX' },
  dexada_spot:  { color: '#0033AD', letter: 'A', label: 'DEX Cardano' },
  dexavax_spot: { color: '#E84142', letter: 'A', label: 'DEX Avalanche' },
  dexsui_spot:  { color: '#4DA2FF', letter: 'S', label: 'DEX Sui' },
  dexnear_spot: { color: '#00EC97', letter: 'N', label: 'DEX NEAR' },
  // Иконка — cryptocom.png через spotBaseExchange().
  cryptocom_spot: { color: '#063EAA', letter: 'C', label: `Crypto.com ${L_SPOT}` },
  // Иконка — coinbase.png через spotBaseExchange().
  coinbase_spot: { color: '#0052FF', letter: 'C', label: `Coinbase ${L_SPOT}` },
  binalpha:     { color: '#F0B90B', letter: 'α', label: 'Binance Alpha' },

  // USDC-M и COIN-M (инверсные) перпы — отдельные площадки матрицы, только
  // данные. Логотип — базовой биржи, в углу метка USDC/COIN (см. MarginBadge).
  binance_usdc:  { color: '#F3BA2F', letter: 'B', label: 'Binance USDC' },
  binance_coinm: { color: '#F3BA2F', letter: 'B', label: 'Binance COIN-M' },
  bybit_usdc:    { color: '#F7A600', letter: 'Y', label: 'Bybit USDC' },
  bybit_inverse: { color: '#F7A600', letter: 'Y', label: 'Bybit COIN-M' },
  okx_coinm:     { color: '#FFFFFF', letter: 'X', label: 'OKX COIN-M' },
  bitget_usdc:   { color: '#1DA2B4', letter: 'S', label: 'Bitget USDC' },
  mexc_usdc:     { color: '#1972E2', letter: 'M', label: 'MEXC USDC' },
  mexc_coinm:    { color: '#1972E2', letter: 'M', label: 'MEXC COIN-M' },
  kucoin_usdc:   { color: '#23AF91', letter: 'K', label: 'KuCoin USDC' },
  kucoin_coinm:  { color: '#23AF91', letter: 'K', label: 'KuCoin COIN-M' },
  phemex_usdc:   { color: '#39C4B6', letter: 'P', label: 'Phemex USDC' },
  phemex_coinm:  { color: '#39C4B6', letter: 'P', label: 'Phemex COIN-M' },
  kraken_coinm:  { color: '#5848D6', letter: 'K', label: 'Kraken COIN-M' },
  gate_coinm:    { color: '#2354E6', letter: 'G', label: 'Gate COIN-M' },
};

/** Спот-площадка? Нужно и для иконки (берём картинку базовой биржи), и для
 *  пометки «спот» в интерфейсе. */
export const isSpotVenue = (ex: string): boolean =>
  /_spot$/.test(ex.toLowerCase()) || ex.toLowerCase() === 'binalpha';

/** Builder-декс Hyperliquid (`hl_xyz` и т.п.)? Иконка — базовая HL, в углу
 *  метка с буквой декса: в мобильных таблицах видны только иконки, и без
 *  метки «hyna → Hyperliquid» выглядел как «HL → HL». */
export const isHlDex = (ex: string): boolean => /^hl_/.test(ex.toLowerCase());

/** Базовая биржа спот-площадки: `binance_spot` → `binance` (для иконки/ссылок). */
// Спот Lighter: наша база → имя рынка в адресе приложения (см. spot.py).
const LIGHTER_SPOT_APP_NAME: Record<string, string> = {
  WEETH: 'weETH', SPY: 'rhSPY', QQQ: 'rhQQQ', GOLD: 'XAUT',
};

export const spotBaseExchange = (ex: string): string => {
  const k = ex.toLowerCase();
  if (k === 'binalpha') return 'binance';
  if (k === 'nonkycusdc_spot') return 'nonkyc';
  return k.replace(/_spot$/, '');
};

const SUPPORTED_PNG = new Set([
  'aster', 'binance', 'bingx', 'bitget', 'bybit',
  'gate', 'htx', 'hyperliquid', 'kucoin', 'mexc', 'okx', 'phemex',
  'bitmart', 'coinex', 'xt', 'btse', 'toobit', 'bitunix',
  'paradex', 'pacifica', 'apex', 'edgex', 'extended', 'grvt',
  // Добавлены 2026-05-22 — иконки для оставшихся 8 view-only бирж.
  // GateDEX делит брэндинг с Gate (тот же G + зелёный квадрат), просто
  // переиспользуем gate.png.
  'ascendex', 'blofin', 'bluefin', 'coinw', 'gatedex', 'nado', 'pionex', 'poloniex',
  // 2026-05-23 batch: 6 новых бирж — dYdX v4, Aevo, Crypto.com, WhiteBIT, BitMEX, Backpack
  'whitebit', 'bitmex', 'cryptocom', 'backpack', 'dydx', 'aevo',
  // LBank (перпы + спот, 2026-08-10). Спот-площадка `lbank_spot` берёт эту же
  // иконку через spotBaseExchange() и метку «S» в углу.
  'lbank',
  // 27.09: логотипы с сайтов бирж; файлы положены 26.10 (orderly.png — логотип
  // Raydium с perps.raydium.io, ключи и ссылки у нас через Raydium; risex.png — rise.trade).
  'orderly', 'risex',
  // 15.10: DEX-спот — логотип сети (Trust Wallet assets), метка «S» в углу как у спота.
  'dexsol', 'dexbsc', 'dexbase', 'dexegld',
  // 16.10: Cardano, Avalanche, Sui, NEAR — тоже логотипы сетей (Trust Wallet assets).
  'dexada', 'dexavax', 'dexsui', 'dexnear',
  // 30.09: Coinbase (перпы INTX и спот) — логотип Coinbase.
  'coinbase',
  // 03.10: Darkex (спот) — иконка PWA с darkex.com.
  'darkex',
  // 10.10: Arcus — иконка с app.arcus.xyz.
  'arcus',
]);
// Иконки, которые лежат как SVG (а не PNG). Поддерживаем оба формата чтобы
// не зависеть от rasterize-инструмента — браузер умеет всё.
const SUPPORTED_SVG = new Set(['kraken', 'lighter']);
// Bitstamp иконка пришла как JPEG (CoinGecko CDN), не перекодируем — браузеры
// рендерят .jpg одинаково. Отдельный set чтобы не путать с PNG.
const SUPPORTED_JPG = new Set(['bitstamp']);
// NonKYC favicon — webp (золотая печать с гравировкой «NKYC»).
const SUPPORTED_WEBP = new Set(['nonkyc']);

/** Путь к логотипу площадки (как у <ExchangeIcon>) или null — своего файла
 *  нет. Нужен там, где рисуем сами, без React: картинка «Поделиться». */
export function exchangeIconSrc(ex: string): string | null {
  const raw = ex.toLowerCase();
  const key = isSpotVenue(raw) ? spotBaseExchange(raw)
            : isHlDex(raw) ? 'hyperliquid'
            : isMarginVenue(raw) ? marginBaseExchange(raw)
            : raw === 'edgex_arc' ? 'edgex'
            : raw === 'lighterhood' ? 'lighter'
            : raw;
  const ext = SUPPORTED_PNG.has(key) ? 'png'
            : SUPPORTED_SVG.has(key) ? 'svg'
            : SUPPORTED_JPG.has(key) ? 'jpg'
            : SUPPORTED_WEBP.has(key) ? 'webp'
            : null;
  return ext ? `/exchanges/${key}.${ext}` : null;
}

export function exchangeLabel(ex: string): string {
  return EX_META[ex.toLowerCase()]?.label || ex.toUpperCase();
}

/**
 * Имя площадки для таблиц: дексы HL — НЕ пять разных бирж, а один
 * «hyperliquid» с маленькой пометкой декса (xyz/para/hyna/mkts) рядом.
 * Для остальных площадок — обычный exchangeLabel.
 */
export const VenueName: React.FC<{ ex: string }> = ({ ex }) => {
  const key = ex.toLowerCase();
  if (!isHlDex(key)) return <>{exchangeLabel(ex)}</>;
  return (
    <>
      Hyperliquid
      <span
        className="ml-1 rounded bg-muted px-1 py-px text-[10px] font-medium leading-none text-muted-foreground align-middle"
        title={EX_META[key]?.label || key}
      >
        {key.slice(3)}
      </span>
    </>
  );
};

/**
 * URL биржевой страницы тикера. `symbol` ожидается в формате 'BTCUSDT' (без separator).
 * Используется для кликабельных названий бирж в funding-плашках и т.п.
 * Возвращает null если паттерн неизвестен (биржа без публичной trade-страницы либо мы её не учли).
 */
/** Преобразует BingX native (NCSKSPACEXV2USD-USDT) в красивый URL формат `SPACEX(VNTL)-USDT`. */
// Имя спот-пары в адресе приложения Hyperliquid, когда оно отличается от
// нативного имени токена в API. Копия HL_SPOT_APP_NAME из
// backend_v2/symbol_normalize.py — там же история, как это снималось.
const HL_SPOT_APP_NAME: Record<string, string> = {
  UBTC: 'BTC', UETH: 'ETH', USOL: 'SOL', UZEC: 'ZEC', UPUMP: 'PUMP',
  UFART: 'FARTCOIN', UXPL: 'XPL', UENA: 'ENA', UMON: 'MON',
  UAVAX: 'AVAX', UVIRT: 'VIRTUAL', UUUSPX: 'SPX', UBONK: 'BONK',
  HPENGU: 'PENGU', HSEI: 'SEI', HNIL: 'NIL', XAUT0: 'XAUT',
};

function bingxPrettyUrl(native: string): string | null {
  // NCSK<TICKER>V2USD → <TICKER>(VNTL)  — Ventures Labs
  // NCSK<TICKER>P2USD → <TICKER>(PreStocks)
  // NCSK<TICKER>2USD  → <TICKER>(NCSK)
  // NCCO<TICKER>2USD  → <TICKER>(NCCO)
  // <TICKER>X-USDT    → <TICKER>(xStocks)
  let m: RegExpMatchArray | null;
  m = native.match(/^NCSK([A-Z0-9]+)V2USD-USDT$/);
  if (m) return `https://bingx.com/en/perpetual/${m[1]}(VNTL)-USDT`;
  m = native.match(/^NCSK([A-Z0-9]+)P2USD-USDT$/);
  if (m) return `https://bingx.com/en/perpetual/${m[1]}(PreStocks)-USDT`;
  m = native.match(/^NCSK(.+)2USD-USDT$/);
  if (m) return `https://bingx.com/en/perpetual/${m[1]}(NCSK)-USDT`;
  m = native.match(/^NCCO(.+)2USD-USDT$/);
  if (m) return `https://bingx.com/en/perpetual/${m[1]}(NCCO)-USDT`;
  m = native.match(/^([A-Z0-9]+)X-USDT$/);
  if (m && m[1].length >= 2) {
    return `https://bingx.com/en/perpetual/${m[1]}(xStocks)-USDT`;
  }
  return null;
}

export function exchangeTickerUrl(
  ex: string, symbol: string,
  hlNative?: string | null, bingxNative?: string | null,
  nativeSym?: string | null
): string | null {
  const sym = (symbol || '').toUpperCase().trim();
  if (!sym) return null;
  const base = sym.endsWith('USDT') ? sym.slice(0, -4) : sym;
  const sep = `${base}_USDT`;     // BTC_USDT — Gate/MEXC формат
  const dash = `${base}-USDT`;    // BTC-USDT — OKX без -SWAP

  // Спот ведём на СПОТОВУЮ страницу: с фьючерсного URL человек попадёт не туда,
  // где увидит ту цену, по которой мы посчитали связку.
  if (isDexVenue(ex)) return dexSwapUrl(ex, base);
  if (isSpotVenue(ex)) {
    switch (spotBaseExchange(ex)) {
      case 'binance': return ex.toLowerCase() === 'binalpha'
        ? `https://www.binance.com/en/alpha/bsc/${base}`
        : `https://www.binance.com/en/trade/${sep}?type=spot`;
      case 'bybit':   return `https://www.bybit.com/en/trade/spot/${base}/USDT`;
      case 'okx':     return `https://www.okx.com/trade-spot/${dash.toLowerCase()}`;
      case 'gate':    return `https://www.gate.io/trade/${sep}`;
      case 'mexc':    return `https://www.mexc.com/exchange/${sep}`;
      case 'bitget':  return `https://www.bitget.com/spot/${sym}`;
      case 'kucoin':  return `https://www.kucoin.com/trade/${base}-USDT`;
      case 'lbank':   return `https://www.lbank.com/trade/${base.toLowerCase()}_usdt`;
      // Kraken Spot: USD-пара есть почти у каждой монеты (USDT — лишь у ~50),
      // поэтому ведём на USD-страницу Kraken Pro.
      case 'kraken':  return `https://pro.kraken.com/app/trade/${base.toLowerCase()}-usd`;
      case 'nonkyc':  return `https://nonkyc.io/market/${base}_${ex.toLowerCase() === 'nonkycusdc_spot' ? 'USDC' : 'USDT'}`;
      case 'darkex':  return `https://www.darkex.com/en/trade/${base}_USDT`;
      // Backpack: на споте только акции США — рынок `MU.US_USDC`; сайт сам
      // уводит `_USDC` на `_USD`, ведём сразу туда.
      case 'backpack': return `https://backpack.exchange/trade/${base}.US_USD`;
      // Lighter: рынок `ETH_USDC`; токены акций Robinhood и weETH — родным
      // регистром (rhSPY, weETH). Таблица — с lighter_spot_base в spot.py.
      case 'lighter': return `https://app.lighter.xyz/trade/${LIGHTER_SPOT_APP_NAME[base] ?? base}_USDC`;
      // Crypto.com: у монеты пара за USD (их ~416) или за USDT (~123) — бэкенд
      // берёт более ликвидную; в native_sym её нет, поэтому ведём на USD.
      case 'cryptocom': return `https://crypto.com/exchange/trade/${base}_USD`;
      // Coinbase Advanced Trade: пары за USD. Имя монеты — из native_sym
      // (наш VELODROME у Coinbase — VELO).
      case 'coinbase': {
        const cb = (nativeSym || sym).toUpperCase().replace(/USDT$/, '');
        return `https://www.coinbase.com/advanced-trade/spot/${cb}-USD`;
      }
      case 'hyperliquid': {
        // Спот у HL котируется за USDC, а монета называется именем моста
        // (`UBTC`, `LINK0`) — вести по нашему канону значит вести на 404.
        // Берём имя площадки из native_sym, он приходит вместе с котировкой.
        // Нативное имя приложение принимает в адресе не всегда: Unit-токены
        // и часть других оно зовёт без приставки (UBTC → BTC, HPENGU →
        // PENGU, XAUT0 → XAUT), а LINK0/AAVE0/FXRP/BNB1/HTAO и токен-акции —
        // нативным. Неверное имя не даёт 404, а молча откатывает на HYPE.
        // Таблица та же, что HL_SPOT_APP_NAME в backend_v2/symbol_normalize.py.
        const hb = (nativeSym || sym).replace(/USDT$/, '').toUpperCase();
        return `https://app.hyperliquid.xyz/trade/${HL_SPOT_APP_NAME[hb] ?? hb}/USDC`;
      }
      default:        return null;
    }
  }

  // USDC-M/COIN-M: база — с множителем биржи (PEPE на binance_usdc — это
  // 1000PEPEUSDC), поэтому берём её из native_sym. Шаблоны — как в
  // backend_v2/notifier._margin_trade_url.
  if (isMarginVenue(ex)) {
    const mb = (nativeSym || sym).toUpperCase().replace(/USDT$/, '');
    const x = mb === 'BTC' ? 'XBT' : mb;
    switch (ex.toLowerCase()) {
      case 'binance_usdc':  return `https://www.binance.com/en/futures/${mb}USDC`;
      case 'binance_coinm': return `https://www.binance.com/en/delivery/${mb.toLowerCase()}usd_perpetual`;
      case 'bybit_usdc':    return `https://www.bybit.com/trade/futures/usdc/${mb}PERP`;
      case 'bybit_inverse': return `https://www.bybit.com/trade/inverse/${mb}USD`;
      case 'okx_coinm':     return `https://www.okx.com/trade-swap/${mb.toLowerCase()}-usd-swap`;
      case 'bitget_usdc':   return `https://www.bitget.com/futures/usdc/${mb}PERP`;
      case 'mexc_usdc':     return `https://futures.mexc.com/exchange/${mb}_USDC`;
      case 'mexc_coinm':    return `https://futures.mexc.com/exchange/${mb}_USD`;
      case 'kucoin_usdc':   return `https://www.kucoin.com/trade/futures/${x}USDCM`;
      case 'kucoin_coinm':  return `https://www.kucoin.com/trade/futures/${x}USDM`;
      case 'kraken_coinm':  return `https://futures.kraken.com/trade/futures/PI_${x}USD`;
      case 'gate_coinm':    return `https://www.gate.io/futures/BTC/${mb}_USD`;
      // Phemex: /trade/ — спот, контракты на /futures/ (см. case 'phemex').
      case 'phemex_usdc':   return `https://phemex.com/futures/${mb}-USDC`;
      case 'phemex_coinm':  return `https://phemex.com/futures/${mb}-USD`;
      default:              return null;
    }
  }

  switch (ex.toLowerCase()) {
    case 'binance': return `https://www.binance.com/en/futures/${sym}`;
    case 'bybit':   return `https://www.bybit.com/trade/usdt/${sym}`;
    case 'okx':     return `https://www.okx.com/trade-swap/${dash.toLowerCase()}-swap`;
    case 'gate':    return `https://www.gate.io/futures/USDT/${sep}`;
    case 'mexc':    return `https://futures.mexc.com/exchange/${sep}`;
    case 'bitget':  return `https://www.bitget.com/futures/usdt/${sym}`;
    // /trade/BTC-USDT у Phemex открывает СПОТ («Phemex Spot Trading»), перп —
    // /futures/. QNT 2026-10-08: перп только залистили, он стоял на ~0.5 %
    // выше спота, и по нашей же ссылке спред выглядел фейком. В notifier
    // исправлено тогда же, здесь — 2026-10-20.
    case 'phemex':  return `https://phemex.com/futures/${dash}`;  // BTC-USDT
    case 'aster':   return `https://www.asterdex.com/en/trade/pro/futures/${sym}`;
    case 'kucoin': {
      // KuCoin futures используют 'XBT' для биткойна (XBTUSDTM).
      const kBase = base === 'BTC' ? 'XBT' : base;
      return `https://www.kucoin.com/futures/trade/${kBase}USDTM`;
    }
    case 'bingx': {
      // Synthetic-stocks (NCSKSPACEXV2USD → SPACEX(VNTL)) и xStocks
      // (AAPLX → AAPL(xStocks)) используют отдельный URL формат.
      if (bingxNative) {
        const pretty = bingxPrettyUrl(bingxNative);
        if (pretty) return pretty;
      }
      return `https://bingx.com/en/perpetual/${dash}`;                                // BTC-USDT
    }
    case 'hyperliquid': {
      // Builder-deployed perp DEX'ы (xyz/vntl/flx/km/cash) — coin приходит
      // как "xyz:AAPL"; URL должен включать prefix для роутинга на правильный
      // builder-контракт. Для main HL — обычный coin name.
      const coin = hlNative && hlNative.includes(':') ? hlNative : base;
      return `https://app.hyperliquid.xyz/trade/${coin}`;
    }
    case 'hl_xyz': case 'hl_para': case 'hl_hyna': case 'hl_mkts': case 'hl_io': {
      // Отдельные площадки builder-дексов: без hl_native префикс декса
      // восстанавливаем из имени площадки (hl_hyna → hyna:BTC).
      const coin = hlNative && hlNative.includes(':')
        ? hlNative
        : `${ex.toLowerCase().slice(3)}:${base}`;
      return `https://app.hyperliquid.xyz/trade/${coin}`;
    }
    case 'coinbase': {
      // Перпы CDE: продукт из native_sym («BIP-20DEC30-CDE» — у BTC имя
      // другое, угадать нельзя). Без него — список фьючерсов.
      if (nativeSym && /-CDE$/i.test(nativeSym)) {
        return `https://www.coinbase.com/advanced-trade/futures/${nativeSym.toUpperCase()}`;
      }
      if (nativeSym && /-PERP$/i.test(nativeSym)) {
        return `https://www.coinbase.com/advanced-trade/perpetual/${nativeSym.toUpperCase()}-INTX`;
      }
      return 'https://www.coinbase.com/advanced-trade/futures';
    }
    case 'kraken': {
      // Канон ≠ имя на Kraken у токен-акций: AAPL там PF_AAPLXUSD. Берём
      // native_sym (приходит с котировкой), иначе — наш символ.
      const nb = (nativeSym || sym).toUpperCase().replace(/USDT$/, '');
      const kBase = nb === 'BTC' ? 'XBT' : nb;
      return `https://futures.kraken.com/trade/futures/PF_${kBase}USD`;
    }
    case 'htx':         return `https://www.htx.com/futures/linear_swap/exchange?contract_code=${dash}&contract_type=swap`;
    case 'bitmart':     return `https://www.bitmart.com/ru-RU/futures/${sym}?theme=dark`;
    case 'coinex':      return `https://www.coinex.com/en/futures/${sym}`;
    case 'xt':          return `https://www.xt.com/en/futures/trade/${base.toLowerCase()}_usdt`;
    case 'bitunix':     return `https://www.bitunix.com/contract-trade/${sym}`;
    case 'btse':        return `https://www.btse.com/en/futures/${base}PFC`;
    case 'toobit':      return `https://www.toobit.com/en-US/futures/${base}-SWAP-USDT`;
    case 'paradex':     return `https://app.paradex.trade/futures/${base}-USD-PERP`;
    case 'pacifica':    return `https://app.pacifica.fi/trade/${base}`;
    case 'lighter':     return `https://app.lighter.xyz/trade/${base}`;
    case 'lighterhood': return `https://robinhoodchain.lighter.xyz/trade/${base}`;
    // native_sym: PEPE на ApeX торгуется как 1000PEPEUSDT.
    case 'apex':        return `https://omni.apex.exchange/trade/${(nativeSym || sym).toUpperCase()}`;
    case 'edgex':       return `https://pro.edgex.exchange/trade/${base}USD`;
    case 'extended':    return `https://app.extended.exchange/trade/${base}-USD`;
    case 'arcus':       return `https://app.arcus.xyz/trade/perpetuals/${base}-USD`;
    case 'grvt':        return `https://grvt.io/exchange/perpetual/${base}-USDT`;
    // Orderly — общий стакан; открываем у Raydium Perps (брокер на Orderly).
    // Брокерские рынки Orderly зовутся с суффиксом (PERP_BVIV_USDC_mythos) —
    // имя берём из native_sym как есть.
    case 'orderly':     return `https://perps.raydium.io/perp/${
      nativeSym && /^PERP_/i.test(nativeSym) ? nativeSym : `PERP_${base}_USDC`}`;
    case 'edgex_arc':   return 'https://pro.edgex.exchange/arc';
    case 'risex':       return `https://www.rise.trade/en/trade/${base}`;
    case 'lbank':       return `https://www.lbank.com/futures/${base.toLowerCase()}usdt`;
    case 'gatedex':     return `https://web3.gate.com/en/perps/USDT/${base}_USDT`;
    case 'poloniex':    return `https://www.poloniex.com/futures/${base}_USDT_PERP`;
    case 'coinw':       return `https://www.coinw.com/frontfutures/${base.toLowerCase()}usdt`;
    case 'ascendex': {
      // У AscendEX our canonical = `XAGUSDT` (для SILVER из collision-map),
      // а native = `XAG-PERP`. Используем native_sym если есть, иначе fallback
      // на default — для большинства monikers совпадает (BTC-PERP, ETH-PERP).
      const ax = nativeSym || `${base}-PERP`;
      return `https://ascendex.com/en/futures/${ax}`;
    }
    case 'bitstamp':    return `https://www.bitstamp.net/trade/${(nativeSym || `${base.toLowerCase()}usd-perp`).toLowerCase()}/`;
    case 'pionex':      return `https://www.pionex.com/futures/${base}-USDT-PERP/`;
    case 'bluefin': {
      const bf = nativeSym || `${base}-PERP`;
      return `https://trade.bluefin.io/?symbol=${bf}`;
    }
    case 'nado': {
      const nd = nativeSym || `${base}-PERP`;
      return `https://app.nado.xyz/perpetuals?market=${nd}`;
    }
    case 'blofin': {
      // Native у Blofin = `BTC-USDT`. Default — тот же шаблон, native_sym
      // редко нужен, но если есть — используем его.
      const bf = nativeSym || `${base}-USDT`;
      return `https://blofin.com/futures/${bf}`;
    }
    case 'nonkyc':    return `https://perp.nonkyc.io/perp/PERP_${base}_USDC/`;
    case 'whitebit':  return `https://whitebit.com/futures/${base}_PERP`;
    case 'bitmex': {
      const b = base === 'BTC' ? 'XBT' : base;
      return `https://www.bitmex.com/app/trade/${b}USDT`;
    }
    case 'cryptocom': return `https://crypto.com/exchange/trade/${base}USD-PERP`;
    case 'backpack':  return `https://backpack.exchange/trade/${base}_USDC_PERP`;
    case 'dydx':      return `https://dydx.trade/trade/${base}-USD`;
    case 'aevo':      return `https://app.aevo.xyz/perpetual/${base}`;
    default:        return null;
  }
}

/** Иконка со меткой спота в углу. На телефоне в таблицах видны ТОЛЬКО иконки,
 *  а логотип у спота тот же, что у перпа — без метки «Binance Alpha → MEXC спот»
 *  и «Binance → MEXC» выглядели одинаково. */
/** Метка builder-декса HL в углу иконки — буква декса на бирюзовом фоне HL.
 *  Тот же приём, что у SpotBadge: на телефоне подписи нет, различает метка. */
const HlDexBadge: React.FC<{ ex: string; size: number }> = ({ ex, size }) => {
  const m = EX_META[ex.toLowerCase()];
  const s = Math.max(8, Math.round(size * 0.55));
  return (
    <span
      title={m?.label || ex}
      className="pointer-events-none absolute -bottom-0.5 -right-0.5 inline-flex items-center justify-center rounded-full font-bold"
      style={{
        width: s, height: s, fontSize: s * 0.72, lineHeight: 1,
        background: '#97FCE4', color: '#0a0a0a',
        boxShadow: '0 0 0 1.5px rgba(10,10,10,0.9)',
      }}
    >
      {m?.letter || ex.slice(3, 4).toUpperCase()}
    </span>
  );
};

/** Метка USDC-M/COIN-M сегмента: логотип у сегмента тот же, что у USDT-перпа
 *  биржи, и без метки «Binance → Binance USDC» в мобильной таблице выглядело бы
 *  как связка биржи самой с собой. USDC — синяя (цвет монеты), COIN — оранжевая. */
const MarginBadge: React.FC<{ ex: string; size: number }> = ({ ex, size }) => {
  const kind = marginKind(ex);
  const h = Math.max(7, Math.round(size * 0.42));
  return (
    <span
      title={EX_META[ex.toLowerCase()]?.label || ex}
      className="pointer-events-none absolute -bottom-1 -right-1.5 inline-flex items-center justify-center rounded-sm font-bold"
      style={{
        height: h, padding: '0 2px', fontSize: Math.max(5, h * 0.72), lineHeight: 1,
        letterSpacing: '-0.02em',
        background: kind === 'usdc' ? '#2775CA' : '#F7931A',
        color: kind === 'usdc' ? '#ffffff' : '#0a0a0a',
        boxShadow: '0 0 0 1.5px rgba(10,10,10,0.9)',
      }}
    >
      {kind === 'usdc' ? 'USDC' : 'COIN'}
    </span>
  );
};

/** Lighter на Robinhood Chain: логотип Lighter, в углу — перо Robinhood (сеть). */
const RobinhoodBadge: React.FC<{ size: number }> = ({ size }) => {
  const s = Math.max(8, Math.round(size * 0.55));
  return (
    <img
      src="/exchanges/robinhood.png"
      alt=""
      title="Robinhood Chain"
      width={s}
      height={s}
      className="pointer-events-none absolute -bottom-0.5 -right-0.5 rounded-full object-cover"
      style={{ width: s, height: s, boxShadow: '0 0 0 1.5px rgba(10,10,10,0.9)' }}
    />
  );
};

const SpotBadge: React.FC<{ ex: string; size: number }> = ({ ex, size }) => {
  const alpha = ex.toLowerCase() === 'binalpha';
  const s = Math.max(8, Math.round(size * 0.55));
  return (
    <span
      title={alpha ? 'Binance Alpha' : L_SPOT}
      className="pointer-events-none absolute -bottom-0.5 -right-0.5 inline-flex items-center justify-center rounded-full font-bold"
      style={{
        width: s, height: s, fontSize: s * 0.72, lineHeight: 1,
        background: alpha ? '#F0B90B' : '#22c55e', color: '#0a0a0a',
        boxShadow: '0 0 0 1.5px rgba(10,10,10,0.9)',
      }}
    >
      {alpha ? 'α' : 'S'}
    </span>
  );
};

/**
 * `decorative` — иконка стоит вплотную к текстовой подписи биржи и сама по себе
 * ничего не добавляет. Без этого флага её `alt` попадает в поток текста рядом с
 * подписью: при копировании страницы и при не загрузившейся картинке выходит
 * «BinanceBinance». Подсказку (`title`) оставляем в обоих случаях.
 */
export const ExchangeIcon: React.FC<{ ex: string; size?: number; decorative?: boolean }> =
  ({ ex, size = 20, decorative }) => {
  const rawKey = ex.toLowerCase();
  // Своих логотипов у спот-площадок нет — берём картинку базовой биржи, а что
  // это спот, видно из подписи рядом (и из title). У builder-дексов HL — та же
  // история: логотип Hyperliquid, декс различается меткой в углу.
  const key = isSpotVenue(rawKey) ? spotBaseExchange(rawKey)
            : isHlDex(rawKey) ? 'hyperliquid'
            : isMarginVenue(rawKey) ? marginBaseExchange(rawKey)
            // edgeX Arc — аккаунт edgeX: логотип тот же.
            : rawKey === 'edgex_arc' ? 'edgex'
            // Lighter на Robinhood Chain — логотип Lighter, отличие в подписи.
            : rawKey === 'lighterhood' ? 'lighter'
            : rawKey;
  const m = EX_META[rawKey] || EX_META[key] || { color: '#475569', letter: '?', label: ex };
  const [errored, setErrored] = useState(false);
  // Сброс errored при смене ex: инстанс компонента может переиспользоваться
  // (виртуализация/реордер списка) — без сброса один 404 навсегда показывал бы
  // буквенный chip вместо валидной иконки нового exchange.
  useEffect(() => { setErrored(false); }, [key]);

  const ext = SUPPORTED_PNG.has(key) ? 'png'
            : SUPPORTED_SVG.has(key) ? 'svg'
            : SUPPORTED_JPG.has(key) ? 'jpg'
            : SUPPORTED_WEBP.has(key) ? 'webp'
            : null;
  if (ext && !errored) {
    const img = (
      <img
        src={`/exchanges/${key}.${ext}`}
        alt={decorative ? '' : m.label}
        title={m.label}
        width={size}
        height={size}
        loading="lazy"
        onError={() => setErrored(true)}
        className="inline-block rounded-md flex-shrink-0 object-contain"
        style={{ width: size, height: size }}
      />
    );
    if (!isSpotVenue(rawKey) && !isHlDex(rawKey) && !isMarginVenue(rawKey)
        && rawKey !== 'lighterhood') return img;
    return (
      <span className="relative inline-flex flex-shrink-0" style={{ width: size, height: size }}>
        {img}
        {rawKey === 'lighterhood'
          ? <RobinhoodBadge size={size} />
          : isHlDex(rawKey)
          ? <HlDexBadge ex={rawKey} size={size} />
          : isMarginVenue(rawKey)
            ? <MarginBadge ex={rawKey} size={size} />
            : <SpotBadge ex={rawKey} size={size} />}
      </span>
    );
  }

  // Fallback: colored letter chip
  const isLight = m.color === '#FFFFFF' || m.color === '#97FCE4';
  const chip = (
    <span
      className="inline-flex items-center justify-center rounded-md font-bold flex-shrink-0"
      style={{
        width: size, height: size,
        background: m.color,
        color: isLight ? '#0a0a0a' : '#fff',
        fontSize: size * 0.55,
        lineHeight: 1,
      }}
      title={m.label}
    >
      {m.letter}
    </span>
  );
  if (!isMarginVenue(rawKey)) return chip;
  return (
    <span className="relative inline-flex flex-shrink-0" style={{ width: size, height: size }}>
      {chip}
      <MarginBadge ex={rawKey} size={size} />
    </span>
  );
};
